import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaTimes } from 'react-icons/fa';

const normalizeSearch = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const SCROLLBAR_BY_SCHEME = {
  blue: '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-blue-400 [&::-webkit-scrollbar-thumb]:dark:bg-blue-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-blue-500 dark:[&::-webkit-scrollbar-thumb:hover]:bg-blue-400',
  black: '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:dark:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:dark:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-500 dark:[&::-webkit-scrollbar-thumb:hover]:bg-gray-400',
};

const ACCENT_BY_SCHEME = {
  blue: {
    triggerFocus: 'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    menuBorder: 'border-gray-200 dark:border-gray-600',
    searchBorder: 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500',
    selected: 'bg-blue-600 text-white',
    checkIcon: 'text-blue-600',
    tag: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    tagHover: 'hover:bg-blue-200 dark:hover:bg-blue-800',
  },
  black: {
    triggerFocus: 'focus:ring-0 focus:border-black dark:focus:border-gray-900',
    menuBorder: 'border-black dark:border-gray-900',
    searchBorder: 'border-black dark:border-gray-900 focus:border-black dark:focus:border-gray-900 focus:ring-0',
    selected: 'bg-black text-white dark:bg-gray-900',
    checkIcon: 'text-black dark:text-gray-900',
    tag: 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
    tagHover: 'hover:bg-gray-200 dark:hover:bg-gray-700',
  },
};

const CustomMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Selecione...',
  className = '',
  disabled = false,
  label = '',
  singleSelect = false,
  searchable = false,
  searchPlaceholder = 'Digite para buscar...',
  maxVisibleOptions = null,
  colorScheme = 'blue',
  triggerClassName = '',
}) => {
  const accent = ACCENT_BY_SCHEME[colorScheme] || ACCENT_BY_SCHEME.blue;
  const scrollbarClasses = SCROLLBAR_BY_SCHEME[colorScheme] || SCROLLBAR_BY_SCHEME.blue;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
    maxHeight: 240,
  });

  const filteredOptions = useMemo(() => {
    let list;
    if (!searchable || !search.trim()) {
      list = options;
    } else {
      const term = normalizeSearch(search.trim());
      list = options.filter((opt) =>
        normalizeSearch(opt.searchText || opt.label || '').includes(term)
      );
    }
    if (maxVisibleOptions == null) return list;
    return list.slice(0, maxVisibleOptions);
  }, [options, search, searchable, maxVisibleOptions]);

  const updateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const searchOffset = searchable ? 52 : 0;
    const estimatedMenuHeight = Math.min(filteredOptions.length * 44 + 8 + searchOffset, 280);
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

    setMenuPosition({
      left: rect.left,
      width: rect.width,
      openUpward,
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      maxHeight: Math.max(140, Math.min(280, openUpward ? spaceAbove : spaceBelow)),
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const clickedOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      if (clickedOutsideButton && clickedOutsideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateMenuPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
      if (searchable) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    } else {
      setSearch('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- updateMenuPosition is stable for this effect's lifecycle
  }, [isOpen, filteredOptions.length, searchable]);

  const handleToggle = (optionValue) => {
    if (disabled) return;

    if (singleSelect) {
      onChange(optionValue);
      setIsOpen(false);
    } else {
      const currentValues = Array.isArray(value) ? value : [];
      const index = currentValues.indexOf(optionValue);

      let newValues;
      if (index > -1) {
        newValues = currentValues.filter((v) => v !== optionValue);
      } else {
        newValues = [...currentValues, optionValue];
      }

      onChange(newValues);
    }
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    if (singleSelect) {
      onChange('');
    } else {
      const currentValues = Array.isArray(value) ? value : [];
      onChange(currentValues.filter((v) => v !== optionValue));
    }
  };

  const isSelected = (optionValue) => {
    if (singleSelect) {
      return value === optionValue;
    }
    return Array.isArray(value) && value.includes(optionValue);
  };

  const selectedOptions = singleSelect
    ? (value ? options.filter((opt) => opt.value === value) : [])
    : (Array.isArray(value) ? options.filter((opt) => value.includes(opt.value)) : []);

  const listMaxHeight = menuPosition.maxHeight - (searchable ? 52 : 0);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          const nextState = !isOpen;
          setIsOpen(nextState);
          if (nextState) updateMenuPosition();
        }}
        disabled={disabled}
        className={`
          w-full min-h-[46px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
          focus:outline-none ${accent.triggerFocus}
          flex items-center justify-between
          transition-colors
          ${triggerClassName}
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer'}
        `}
      >
        <div className="flex-1 flex flex-wrap gap-1 items-center">
          {selectedOptions.length > 0 ? (
            singleSelect ? (
              <span className="text-gray-900 dark:text-white">
                {selectedOptions[0].label}
              </span>
            ) : (
              selectedOptions.map((option) => (
                <span
                  key={option.value}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${accent.tag}`}
                >
                  {option.label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(option.value, e)}
                      className={`rounded-full p-0.5 ${accent.tagHover}`}
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))
            )
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </div>
        <FaChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed z-[10000] bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden ${
              searchable ? `border ${accent.menuBorder}` : 'border border-gray-200 dark:border-gray-600'
            }`}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              transform: menuPosition.openUpward ? 'translateY(-100%)' : 'none',
            }}
          >
            {searchable && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className={`w-full px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none border ${accent.searchBorder}`}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div
              className={`py-1 overflow-auto ${scrollbarClasses}`}
              style={{ maxHeight: listMaxHeight }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const selected = isSelected(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleToggle(option.value)}
                      className={`
                        w-full px-4 py-2.5 text-left text-[15px]
                        transition-colors flex items-center gap-2
                        border-b border-gray-100 dark:border-gray-600/50 last:border-b-0
                        ${
                          selected
                            ? accent.selected
                            : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                        }
                      `}
                    >
                      <div
                        className={`
                          w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0
                          ${
                            selected
                              ? 'bg-white border-white'
                              : 'border-gray-300 dark:border-gray-500'
                          }
                        `}
                      >
                        {selected && (
                          <svg className={`w-3 h-3 ${accent.checkIcon}`} fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span>{option.label}</span>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma opção encontrada
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomMultiSelect;
